import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileUp, FileText, BarChart2, AlertCircle, User, CheckCircle, ChevronRight, ArrowRight, Star, FileCheck, Clock, MessageCircle, Sparkles, CreditCard, Lock, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useAICoach } from '../contexts/AICoachContext';
import { AICoachInsights } from '../components/AICoachInsights';
import { AICoachChat } from '../components/AICoachChat';
import { JobMatchDashboardCards } from '../components/JobMatchDashboardCards';
import { SubscriptionPrompt } from '../components/SubscriptionPrompt';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { useAccessControl } from '../hooks/useAccessControl';
import { redirectToSubscriptionCheckout } from '../services/stripeService';

// Import question constants to calculate total questions
import { BRAG_QUESTIONS } from '../components/BragSheet';
import { DREAM_QUESTIONS } from '../components/DreamSheet';

interface Document {
  id: string;
  name: string;
  type: string;
  created_at: string;
  status: string;
}

interface Report {
  id: string;
  title: string;
  created_at: string;
  status: string;
}

interface BragItem {
  id: string;
  question: string;
  description: string;
}

interface DreamItem {
  id: string;
  category: string;
  question: string;
  description: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const accessControl = useAccessControl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bragItems, setBragItems] = useState<BragItem[]>([]);
  const [dreamItems, setDreamItems] = useState<DreamItem[]>([]);
  const [bragCompletion, setBragCompletion] = useState(0);
  const [dreamCompletion, setDreamCompletion] = useState(0);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [readyForReport, setReadyForReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAICoachChat, setShowAICoachChat] = useState(false);
  const [redirectingToSubscription, setRedirectingToSubscription] = useState(false);
  const [intendedTier, setIntendedTier] = useState<string | null>(null);

  // Handle setup success redirect to subscription checkout
  useEffect(() => {
    const setupSuccess = searchParams.get('setup');
    const tierParam = searchParams.get('tier');

    if (setupSuccess === 'success' && tierParam && user) {
      console.log('Setup fee payment successful, redirecting to subscription checkout for tier:', tierParam);

      // Remove query params to prevent duplicate redirects
      setSearchParams({});

      // Set redirecting state
      setRedirectingToSubscription(true);

      // Wait a moment for webhook to process, then redirect to subscription checkout
      setTimeout(async () => {
        try {
          toast.success('Setup payment complete! Now choose your subscription plan.');
          await redirectToSubscriptionCheckout({
            tier: tierParam,
            successUrl: `${window.location.origin}/dashboard?subscription=success`,
            cancelUrl: `${window.location.origin}/dashboard?subscription=cancel`,
          });
        } catch (error) {
          console.error('Error redirecting to subscription checkout:', error);
          toast.error('Please select your subscription plan to continue');
          setRedirectingToSubscription(false);
          // Redirect to subscription page as fallback
          window.location.href = '/subscription?tab=plans';
        }
      }, 2000); // 2 second delay to allow webhook processing
    }
  }, [searchParams, user]);

  // Re-check subscription status when returning from successful payment
  useEffect(() => {
    const subscriptionSuccess = searchParams.get('subscription');
    const reportSuccess = searchParams.get('report');

    if ((subscriptionSuccess === 'success' || reportSuccess === 'success') && user) {
      console.log('Payment successful, re-checking subscription status...');

      // Wait for webhook to process before checking
      const timer = setTimeout(async () => {
        try {
          await accessControl.refresh();

          if (accessControl.hasSetupFeePaid) {
            toast.success('Payment received! Your access has been updated.');
          }

          // Clean up URL params
          setSearchParams({});
        } catch (error) {
          console.error('Error re-checking subscription status:', error);
        }
      }, 1500); // 1.5 second delay to allow webhook processing

      return () => clearTimeout(timer);
    }
  }, [searchParams, user, accessControl]);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    const abortController = new AbortController();

    const checkIntendedTier = async () => {
      try {
        // Check for intended tier if setup fee is paid but no active subscription
        if (accessControl.hasSetupFeePaid && !accessControl.hasActiveSubscription) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('intended_tier, subscription_workflow_step')
            .eq('id', user.id)
            .maybeSingle();

          if (isMounted && profile) {
            setIntendedTier(profile.intended_tier);

            // If workflow is stuck at setup_paid, user needs to complete subscription
            if (profile.subscription_workflow_step === 'setup_paid' && profile.intended_tier) {
              console.log('User has setup fee paid but no subscription. Intended tier:', profile.intended_tier);
            }
          }
        }
      } catch (error) {
        console.error('Error checking intended tier:', error);
      }
    };

    const fetchDashboardData = async () => {
      const timerLabel = `Dashboard-Fetch-${Date.now()}`;
      console.time(timerLabel);
      console.log('🚀 Dashboard: Starting dashboard data fetch for user:', user.id);

      if (isMounted) {
        setIsLoading(true);
        setError(null);
      }

      // Add timeout protection
      const timeoutId = setTimeout(() => {
        console.timeEnd(timerLabel);
        console.error('⏰ Dashboard: Request timeout reached after 15 seconds');
        if (isMounted) {
          setIsLoading(false);
          setError('Request timed out. Please try refreshing the page.');
        }
        abortController.abort();
      }, 15000); // 15 second timeout

      try {
        console.log('📡 Dashboard: Loading dashboard data with timeout protection...');

        // Set default empty data
        let docsData: Document[] = [];
        let reportsData: Report[] = [];
        let bragItemsData: BragItem[] = [];
        let dreamItemsData: DreamItem[] = [];

        // Try to load data with individual timeouts for each query
        try {
          console.log('📡 Loading documents...');
          const docsPromise = supabase.from('documents').select('*').eq('user_id', user.id);
          const docsResult = await Promise.race([
            docsPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Documents query timeout')), 3000))
          ]);
          docsData = (docsResult as any)?.data || [];
        } catch (err) {
          console.warn('Could not load documents:', err);
        }

        try {
          console.log('📡 Loading reports...');
          const reportsPromise = supabase.from('reports').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
          const reportsResult = await Promise.race([
            reportsPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Reports query timeout')), 3000))
          ]);
          reportsData = (reportsResult as any)?.data || [];
        } catch (err) {
          console.warn('Could not load reports:', err);
        }

        // Load BRAG items
        try {
          console.log('📡 Loading BRAG items...');
          const bragPromise = supabase
            .from('brag_items')
            .select(`
              id,
              question,
              description,
              created_at,
              brag_sheets!inner(user_id)
            `)
            .eq('brag_sheets.user_id', user.id);
          const bragResult = await Promise.race([
            bragPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('BRAG items query timeout')), 3000))
          ]);
          bragItemsData = (bragResult as any)?.data || [];
          console.log('✅ Loaded BRAG items:', bragItemsData.length);
        } catch (err) {
          console.warn('Could not load BRAG items:', err);
        }

        // Load DREAM items
        try {
          console.log('📡 Loading DREAM items...');
          const dreamPromise = supabase
            .from('dream_items')
            .select(`
              id,
              category,
              question,
              description,
              created_at,
              dream_sheets!inner(user_id)
            `)
            .eq('dream_sheets.user_id', user.id);
          const dreamResult = await Promise.race([
            dreamPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('DREAM items query timeout')), 3000))
          ]);
          dreamItemsData = (dreamResult as any)?.data || [];
          console.log('✅ Loaded DREAM items:', dreamItemsData.length);
        } catch (err) {
          console.warn('Could not load DREAM items:', err);
        }

        clearTimeout(timeoutId);

        // Calculate BRAG completion (only count meaningful responses >= 10 characters)
        const BRAG_TOTAL = 20;
        const validBragItems = bragItemsData.filter(item =>
          item.description &&
          item.description.trim().length >= 10
        );
        const bragCompletionCalc = Math.round((validBragItems.length / BRAG_TOTAL) * 100);

        // Calculate DREAM completion (only count meaningful responses >= 10 characters)
        const DREAM_TOTAL = 25;
        const validDreamItems = dreamItemsData.filter(item =>
          item.description &&
          item.description.trim().length >= 10
        );
        const dreamCompletionCalc = Math.round((validDreamItems.length / DREAM_TOTAL) * 100);

        console.log('📊 BRAG Completion:', validBragItems.length, 'of', BRAG_TOTAL, '=', bragCompletionCalc + '%');
        console.log('📊 DREAM Completion:', validDreamItems.length, 'of', DREAM_TOTAL, '=', dreamCompletionCalc + '%');

        // Calculate basic completion
        const profileCompletionCalc = docsData.length > 0 ? 40 : 0;

        const dashboardData = {
          documents: docsData,
          reports: reportsData,
          bragItems: bragItemsData,
          dreamItems: dreamItemsData,
          bragCompletion: bragCompletionCalc,
          dreamCompletion: dreamCompletionCalc,
          profileCompletion: profileCompletionCalc,
          readyForReport: false
        };

        console.log('📊 Dashboard: Processing dashboard data...');
        console.log('📊 Dashboard: Documents count:', dashboardData.documents?.length || 0);
        console.log('📊 Dashboard: Reports count:', dashboardData.reports?.length || 0);
        console.log('📊 Dashboard: BRAG items count:', dashboardData.bragItems?.length || 0);
        console.log('📊 Dashboard: DREAM items count:', dashboardData.dreamItems?.length || 0);

        // Update all state with the consolidated data only if component is still mounted
        if (isMounted) {
          setDocuments(dashboardData.documents || []);
          setReports(dashboardData.reports || []);
          setBragItems(dashboardData.bragItems || []);
          setDreamItems(dashboardData.dreamItems || []);
          setBragCompletion(dashboardData.bragCompletion || 0);
          setDreamCompletion(dashboardData.dreamCompletion || 0);
          setProfileCompletion(dashboardData.profileCompletion || 0);
          setReadyForReport(dashboardData.readyForReport || false);
        }

        console.log('✅ Dashboard: State updated successfully');
        console.timeEnd(timerLabel);
      } catch (error) {
        clearTimeout(timeoutId);
        console.timeEnd(timerLabel);
        console.error('Dashboard - Error:', error);

        if (isMounted) {
          setError('Failed to load dashboard data. Please refresh the page.');
          toast.error('Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkIntendedTier();
    fetchDashboardData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [user?.id, accessControl.hasSetupFeePaid, accessControl.hasActiveSubscription]);

  // Show redirecting state
  if (redirectingToSubscription) {
    return (
      <div className="py-12">
        <div className="container">
          <div className="text-center max-w-md mx-auto">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Setup Payment Complete!</h2>
            <p className="text-muted-foreground mb-2">
              Redirecting you to select your subscription plan...
            </p>
            <p className="text-sm text-gray-500">
              This should only take a moment
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there was a critical error
  if (error) {
    return (
      <div className="py-12">
        <div className="container">
          <div className="text-center max-w-md mx-auto">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Loading Error</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const documentCount = documents.length;
  const reportCount = reports.length;
  const hasCompletedDocuments = documents.filter(doc => doc.type === 'resume').length >= 1;

  // Correct total questions based on actual framework structure
  const totalBragQuestions = 20; // 5 questions in each of 4 sections (B, R, A, G)
  const totalDreamQuestions = 25; // 5 questions in each of 5 sections (D, R, E, A, M)

  // Calculate minimum required questions for completion (80%)
  const minBragQuestions = Math.ceil(totalBragQuestions * 0.8); // 16 questions
  const minDreamQuestions = Math.ceil(totalDreamQuestions * 0.8); // 20 questions

  return (
    <div className="py-12">
      <div className="container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground">
              Your career transformation journey starts here
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
              <p className="text-xs text-gray-400 mt-2">
                This should only take a moment
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Subscription Status Banners - Uses centralized access control */}
            {!accessControl.loading && accessControl.hasSetupFeePaid && !accessControl.hasActiveSubscription && intendedTier && (
              <SubscriptionPrompt
                type="setup_complete_need_subscription"
                intendedTier={intendedTier}
                onSubscribeClick={async () => {
                  try {
                    await redirectToSubscriptionCheckout({
                      tier: intendedTier,
                      successUrl: `${window.location.origin}/dashboard?subscription=success`,
                      cancelUrl: `${window.location.origin}/dashboard?subscription=cancel`,
                    });
                  } catch (error) {
                    console.error('Error redirecting to subscription checkout:', error);
                    toast.error('Failed to redirect. Please try again.');
                  }
                }}
              />
            )}

            {!accessControl.loading && accessControl.needsSetupFee && (
              <SubscriptionPrompt type="setup_fee" />
            )}

            {/* AI Coach Quick Access Banner */}
            <div className="mb-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Chat with Your AI Career Coach</h2>
                    <p className="opacity-90">
                      Get personalized guidance, ask questions, and receive career advice
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowAICoachChat(true)}
                    className="bg-white text-blue-600 hover:bg-white/90"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Start Chat
                  </Button>
                  <Link to="/ai-coach">
                    <Button variant="outline" className="border-2 border-white bg-transparent text-white hover:bg-white/20 hover:text-white">
                      View All Conversations
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* AI Coach Insights */}
            <div className="mb-8">
              <AICoachInsights />
            </div>

            {/* Job Match AI Section */}
            <div className="mb-8">
              <JobMatchDashboardCards />
            </div>

            {/* Progress Overview */}
            <div className="bg-white rounded-xl border border-border/40 p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">Profile Completion</h2>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    profileCompletion === 100
                      ? 'bg-green-500'
                      : profileCompletion >= 80
                      ? 'bg-primary'
                      : profileCompletion >= 50
                      ? 'bg-blue-500'
                      : 'bg-amber-500'
                  }`}
                  style={{ width: `${profileCompletion}%` }}
                ></div>
              </div>
              
              <div className="flex items-center mb-4">
                <span className="font-medium">{profileCompletion}% Complete</span>
                {profileCompletion >= 80 && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    Ready for Analysis
                  </span>
                )}
                {profileCompletion < 80 && profileCompletion >= 50 && (
                  <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    Good Progress
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`rounded-lg p-4 ${documents.some(d => d.type === 'resume') ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className={`h-5 w-5 ${documents.some(d => d.type === 'resume') ? 'text-green-600' : 'text-gray-500'} mr-2`} />
                      <span className="font-medium">Resume</span>
                    </div>
                    {documents.some(d => d.type === 'resume') ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Clock className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                <div className={`rounded-lg p-4 ${bragCompletion >= 80 ? 'bg-green-50 border border-green-200' : bragCompletion > 0 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Star className={`h-5 w-5 ${bragCompletion >= 80 ? 'text-green-600' : bragCompletion > 0 ? 'text-blue-600' : 'text-gray-500'} mr-2`} />
                      <span className="font-medium">BRAG Sheet</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {bragCompletion > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          {bragCompletion}%
                        </span>
                      )}
                      {bragCompletion >= 80 ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : bragCompletion > 0 ? (
                        <Clock className="h-5 w-5 text-blue-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                <div className={`rounded-lg p-4 ${dreamCompletion >= 80 ? 'bg-green-50 border border-green-200' : dreamCompletion > 0 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Star className={`h-5 w-5 ${dreamCompletion >= 80 ? 'text-green-600' : dreamCompletion > 0 ? 'text-blue-600' : 'text-gray-500'} mr-2`} />
                      <span className="font-medium">DREAM Sheet</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {dreamCompletion > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          {dreamCompletion}%
                        </span>
                      )}
                      {dreamCompletion >= 80 ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : dreamCompletion > 0 ? (
                        <Clock className="h-5 w-5 text-blue-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <Link to="/profile">
                  <Button variant={profileCompletion < 80 ? "default" : "outline"}>
                    {profileCompletion < 80 ? "Complete Your Profile" : "Edit Profile"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl border border-border/40 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-muted-foreground text-sm">Uploaded Documents</p>
                    <h3 className="text-3xl font-bold mt-2">{documentCount}</h3>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <FileCheck className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="mt-4">
                  <Link to="/profile" className="text-primary text-sm font-medium">
                    Manage documents →
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-border/40 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-muted-foreground text-sm">Career Reports</p>
                    <h3 className="text-3xl font-bold mt-2">{reportCount}</h3>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center">
                    <BarChart2 className="h-5 w-5 text-secondary" />
                  </div>
                </div>
                <div className="mt-4">
                  <Link to="/reports" className="text-secondary text-sm font-medium">
                    View all reports →
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-border/40 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-muted-foreground text-sm">Profile Status</p>
                    <h3 className="text-2xl font-bold mt-2">
                      {profileCompletion >= 80 ? (
                        <span className="text-green-600">Ready</span>
                      ) : profileCompletion >= 50 ? (
                        <span className="text-blue-600">In Progress</span>
                      ) : (
                        <span className="text-amber-600">Getting Started</span>
                      )}
                    </h3>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-accent" />
                  </div>
                </div>
                <div className="mt-4">
                  {readyForReport ? (
                    <Link to="/reports" className="text-accent text-sm font-medium">
                      Generate a report →
                    </Link>
                  ) : (
                    <Link to="/profile" className="text-accent text-sm font-medium">
                      Complete profile →
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Next Steps */}
            {!readyForReport && (
              <div className="bg-white rounded-xl border border-border/40 p-6 mb-12">
                <h2 className="text-xl font-semibold mb-6">Next Steps</h2>
                <div className="space-y-4">
                  {!documents.some(d => d.type === 'resume') && (
                    <div className="flex items-start gap-4 p-4 border border-primary/20 rounded-lg bg-primary/5">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-primary">Upload Your Resume</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Upload your current resume to analyze your professional background
                        </p>
                        <Link to="/profile">
                          <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary/10">
                            Upload Resume
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {bragCompletion < 80 && (
                    <div className="flex items-start gap-4 p-4 border border-secondary/20 rounded-lg bg-secondary/5">
                      <div className="h-8 w-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Star className="h-4 w-4 text-secondary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-secondary">Complete Your BRAG Sheet</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Document your key achievements and skills using our BRAG framework ({bragItems.length}/{totalBragQuestions} questions completed, {minBragQuestions} needed)
                        </p>
                        <Link to="/profile?tab=brag">
                          <Button size="sm" variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
                            {bragItems.length > 0 ? 'Continue BRAG Sheet' : 'Start BRAG Sheet'}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {dreamCompletion < 80 && (
                    <div className="flex items-start gap-4 p-4 border border-accent/20 rounded-lg bg-accent/5">
                      <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Star className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-medium text-accent">Complete Your DREAM Sheet</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Share your aspirations and career goals using our DREAM framework ({dreamItems.length}/{totalDreamQuestions} questions completed, {minDreamQuestions} needed)
                        </p>
                        <Link to="/profile?tab=dream">
                          <Button size="sm" variant="outline" className="border-accent text-accent hover:bg-accent/10">
                            {dreamItems.length > 0 ? 'Continue DREAM Sheet' : 'Start DREAM Sheet'}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Generate Report CTA (Only show if profile is ready) */}
            {profileCompletion >= 80 && reports.length === 0 && (
              <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-8 mb-12 text-white">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">You're Ready for Your Career Analysis!</h2>
                    <p className="opacity-90">
                      Your profile is complete! Generate your comprehensive career report powered by our AI analysis.
                    </p>
                  </div>
                  <Link to="/reports">
                    <Button className="bg-white text-primary hover:bg-white/90">
                      Generate Your Report
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Recent Reports */}
            {reports.length > 0 && (
              <div className="mb-12">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Recent Reports</h2>
                  <Link to="/reports" className="text-primary text-sm font-medium">
                    View all →
                  </Link>
                </div>
                <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
                  <table className="w-full">
                    <caption className="sr-only">List of recent career analysis reports</caption>
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Title
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Generated
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reports.slice(0, 3).map((report) => (
                        <tr key={report.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {report.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {new Date(report.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              report.status === 'completed' ? 'bg-green-100 text-green-800' :
                              report.status === 'processing' ? 'bg-blue-100 text-blue-800' : 
                              report.status === 'error' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {report.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <Link to={`/reports/${report.id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Documents */}
            {documents.length > 0 && (
              <div className="mb-12">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Recent Documents</h2>
                  <Link to="/profile" className="text-primary text-sm font-medium">
                    Manage →
                  </Link>
                </div>
                <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
                  <table className="w-full">
                    <caption className="sr-only">List of recent uploaded documents</caption>
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Uploaded
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {documents.slice(0, 3).map((doc) => (
                        <tr key={doc.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {doc.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">
                            {doc.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              doc.status === 'processed' ? 'bg-green-100 text-green-800' :
                              doc.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              doc.status === 'error' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {doc.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* AI Coach Chat Modal */}
      {showAICoachChat && (
        <AICoachChat
          isOpen={showAICoachChat}
          onClose={() => setShowAICoachChat(false)}
          isModal={true}
        />
      )}
    </div>
  );
};

export default Dashboard;